/* eslint-disable react-native/no-inline-styles */
import React, {Suspense, useEffect, useRef, useState} from 'react';
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {InnerLayoutProps} from '@src/LayoutProvider';
import useIsLoggedIn from '@src/Providers/AuthProvider/useIsLoggedIn';
import HomeContentLoader from 'design-system/src/Components/ContentLoader/HomeContentLoader/HomeContentLoader';
import {createPortal} from 'react-dom';
import AppLayoutConnectLogic from './AppLayoutConnectLogic';
import styled from 'styled-components';
import {responsiveMap} from 'design-system/src/WebComponent/Grid/_utils/responsiveObserve';
import {useAppSelector} from '@src/Store/hooks';
import useBreakpoint from 'design-system/src/WebComponent/Grid/hooks/useBreakpoint';
import useMember from '@src/Store/selectors/useMember';
import useChangeLanguage from '@src/Hooks/useChangeLanguage';
import {useRegionUrl} from '@src/Store/selectors/useRegionUrl';
import {languageMapper, languageUnmap} from '@src/Services/i18n/languageMapper';
import useSetupRegion from '@src/Hooks/useSetupRegion';
import {cultureRegionMapper} from '@src/tools/cultureRegionMapper';
import useGetFooter from '@src/Hooks/useGetFooter';
import ModalManager from '@src/components/shared/ModalManager/ModalManager';
import {getLangFromUrl} from '@src/utils/lang/getLangFromUrl.util';
import {DEFAULT_LANGUAGE} from '@src/Constant/languages';

const RadioPlayerPage = React.lazy(
  () => import('@src/App/components/pages/listen/Radio/RadioPlayerPage'),
);
const AppBottomBar = React.lazy(() => import('./AppBottomBar/AppBottomBar'));
const AppHeader = React.lazy(() => import('./AppHeader/AppHeader'));
const PodCastPlayerPage = React.lazy(
  () => import('@src/App/components/pages/listen/PodCast/PodCastPlayerPage'),
);
const PageFooter = React.lazy(
  () => import('@src/components/general/PageFooter/PageFooter'),
);

// TO DO : cast - app header go back
interface IAppLayoutProps extends InnerLayoutProps {
  children: React.ReactNode;
}
// @ts-ignore
const MainStyled = styled('main')(() => {
  return {
    position: 'relative',
    [`@media ${responsiveMap.mobile}`]: {
      paddingBottom: 92,
    },
  };
});

const AppLayout = (props: IAppLayoutProps) => {
  const {context, children} = props;
  const [isFetchingRegion, setIsFetchingRegion] = useState(true);
  const {fetch} = useSetupRegion(true);
  const {isLoggedIn, isLoading} = useIsLoggedIn();
  const {currentRegion} = useRegionUrl();
  const showLoading = (isLoading && !isLoggedIn) || isFetchingRegion;
  const {isMobile} = useBreakpoint();
  const member = useMember();
  const {updateStoreLang, changeCurrentLanguage, currentLanguage} =
    useChangeLanguage();
  const pageLang = context?.language as string | undefined;
  const footerData = useGetFooter();

  useEffect(() => {
    setIsFetchingRegion(true);
    const unmap = languageUnmap(pageLang || '');
    fetch(unmap).finally(() => {
      setIsFetchingRegion(false);
    });
  }, [pageLang]);

  /** Initial language setup on page load, change language using lang slug from URL */
  useEffect(
    function anonymousLangInit() {
      if (isLoggedIn) return;

      const url = window.location.pathname;
      const lang = getLangFromUrl(url) ?? DEFAULT_LANGUAGE;
      if (lang && lang !== currentLanguage) {
        updateStoreLang(lang);
      }
    },
    [isLoggedIn],
  );

  /** Only 1 time */
  useEffect(
    function memberLangInit() {
      if (!isLoggedIn) return;

      const memberRegionCulture = currentRegion?.defaultLanguage
        ? cultureRegionMapper(currentRegion.defaultLanguage)
        : null;

      const memberRegionPageLang =
        memberRegionCulture && languageMapper(memberRegionCulture);

      const storeLang =
        memberRegionPageLang && languageUnmap(memberRegionPageLang);

      const switchLangIfDifferentFromMember = async () => {
        const shouldUpdateLang =
          !isFetchingRegion &&
          typeof storeLang === 'string' &&
          typeof memberRegionPageLang === 'string' &&
          typeof pageLang === 'string' &&
          (pageLang !== memberRegionPageLang || currentLanguage !== storeLang);

        if (shouldUpdateLang) {
          await changeCurrentLanguage(storeLang as string);
        }
      };

      switchLangIfDifferentFromMember();
    },
    [
      member,
      currentRegion,
      isFetchingRegion,
      pageLang,
      currentLanguage,
      isLoggedIn,
    ],
  );

  const {
    appListen: {isOpenPlayer: isOpenRadioPlayer, isMini: isRadioMini},
    appPodcast: {isOpenPlayer: isOpenPodcastPlayer, isMini: isPodcastMini},
  } = useAppSelector(state => ({
    appListen: state.appListen,
    appPodcast: state.appPodcast,
  }));

  return (
    <div
      className="app-layout"
      style={{
        maxWidth: 1920,
        margin: '0 auto',
      }}>
      {isLoggedIn && <AppLayoutConnectLogic />}
      <Suspense>
        <AppHeader />
      </Suspense>
      <MainStyled>{showLoading ? <HomeContentLoader /> : children}</MainStyled>
      <Suspense>
        <PageFooter {...footerData} />
      </Suspense>
      {createPortal(
        <ToastContainer
          position="bottom-center"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
          style={{zIndex: 99999}}
        />,
        document.body,
        'app-tostify-container',
      )}
      <ModalManager />
      {isOpenPodcastPlayer && (
        <div
          style={{
            height: isPodcastMini ? 64 : 0,
          }}>
          <Suspense>
            <PodCastPlayerPage />
          </Suspense>
        </div>
      )}
      {isOpenRadioPlayer && (
        <div
          style={{
            height: isRadioMini ? 64 : 0,
          }}>
          <Suspense>
            <RadioPlayerPage />
          </Suspense>
        </div>
      )}
      {isMobile && (
        <Suspense>
          <AppBottomBar />
        </Suspense>
      )}
    </div>
  );
};

export default AppLayout;
